import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminProjectsEditor() {
    const [projects, setProjects] = useState([]);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIndex, setOpenIndex] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState({});

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/public/projects", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setProjects(res.data.content || []);
            } catch (error) {
                console.error("Error fetching projects:", error);
                toast.error("Error fetching items");
            } finally {
                setLoading(false);
            }
        };
        
        fetchProjects();
    }, []);

    useEffect(() => {
        const fetchPages = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/content", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setPages(res.data);
            } catch (error) {
                console.error("Error fetching pages:", error);
            }
        };
        
        fetchPages();
    }, []);

    const handleChange = (index, field, value) => {
        const updated = [...projects];
        updated[index][field] = value;
        setProjects(updated);
    };

    const moveProject = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= projects.length) return;

        const updated = [...projects];
        const temp = updated[index];
        updated[index] = updated[newIndex];
        updated[newIndex] = temp;

        setProjects(updated);
        setOpenIndex(newIndex);
    };

    const saveProjects = async () => {
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/projects", { content: projects }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Projects saved!");
        } catch {
            toast.error("Failed to save projects");
        } finally {
            setSaving(false);
        }
    };

    const addProject = () => {
        const newProject = {
            name: "",
            description: "",
            image: "",
            link: "",
            link_label: ""
        };
        setProjects([newProject, ...projects]);
        setOpenIndex(0);
    };

    const deleteProject = async (index) => {
        const project = projects[index];
        const ok = await confirmSwal({
            title: `Delete item ${project.name || "(No Name)"}?`,
            text: "Once deleted it cannot be restored.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        const updated = projects.filter((_, i) => i !== index);
        setProjects(updated);
        setOpenIndex(null);

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/projects", { content: updated }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.info("Item deleted");
        } catch {
            toast.error("Failed to save after deletion");
        }
    };

    const toggleOpen = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleImageUpload = async (index, file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, PNG, and HEIC files are allowed');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setUploading({ ...uploading, [index]: true });

        try {
            const token = await auth.currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('subfolder', 'projects');

            const response = await axios.post('/api/upload-image', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                handleChange(index, 'image', response.data.url);
                toast.success('Image uploaded successfully!');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setUploading({ ...uploading, [index]: false });
        }
    };

    if (loading) return <p>Loading projects...</p>;

    return (
        <div>
            <h4>At a Glance</h4>
            <p className="text-body-secondary">Items here are shown publicly on the homepage.</p>

            <button className="btn btn-admin mb-3" onClick={addProject}>+ Add Item</button>

            <div className="accordion" id="projectsAccordion">
                {projects.map((project, index) => (
                    <div className="accordion-item" key={index}>
                        <h2 className="accordion-header" id={`heading-${index}`}>
                            <button
                                className={`accordion-button ${openIndex === index ? "" : "collapsed"}`}
                                type="button"
                                onClick={() => toggleOpen(index)}
                            >
                                <strong>{project.name || "(No Name)"}</strong>
                            </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${openIndex === index ? "show" : ""}`}>
                            <div className="accordion-body">
                                <div className="mb-2">
                                    <label className="form-label">Name</label>
                                    <input className="form-control" value={project.name} onChange={(e) => handleChange(index, "name", e.target.value)} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" value={project.description} onChange={(e) => handleChange(index, "description", e.target.value)} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Image</label>
                                    <input 
                                        className="form-control mb-2" 
                                        value={project.image} 
                                        onChange={(e) => handleChange(index, "image", e.target.value)}
                                        placeholder="Image URL"
                                    />
                                    <div className="input-group">
                                        <input 
                                            type="file" 
                                            className="form-control" 
                                            accept=".jpg,.jpeg,.png,.heic"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) handleImageUpload(index, file);
                                            }}
                                            disabled={uploading[index]}
                                        />
                                        <button 
                                            className="btn btn-outline-secondary" 
                                            type="button"
                                            disabled
                                        >
                                            {uploading[index] ? 'Uploading...' : 'Upload'}
                                        </button>
                                    </div>
                                    {project.image && (
                                        <div className="mt-2">
                                            <img 
                                                src={project.image} 
                                                alt="Preview" 
                                                style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                                                className="img-thumbnail"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Learn More Link</label>
                                    <select
                                        className="form-select"
                                        value={project.link}
                                        onChange={(e) => handleChange(index, "link", e.target.value)}
                                    >
                                        <option value="">(No link)</option>
                                        {pages.map((p) => (
                                            <option key={p.slug} value={p.slug}>
                                                {p.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Learn More Label <small className="text-body-secondary">(Optional)</small></label>
                                    <input className="form-control" placeholder="Learn More" value={project.link_label || ""} onChange={(e) => handleChange(index, "link_label", e.target.value)} />
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-2"
                                            onClick={() => moveProject(index, -1)}
                                            disabled={index === 0}
                                        >
                                            ↑ Move Up
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => moveProject(index, 1)}
                                            disabled={index === projects.length - 1}
                                        >
                                            ↓ Move Down
                                        </button>
                                    </div>
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => deleteProject(index)} disabled={saving}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <button className="btn btn-admin mt-3" onClick={saveProjects} disabled={saving}>
                {saving ? "Saving..." : "Save"}
            </button>
        </div>
    );
}
